/**
 * Document Page - PDF.js Implementation
 *
 * Replaces Apryse WebViewer for imported PDF documents.
 * Handles:
 * - Embedded PDF form field extraction and filling
 * - Annotations (text boxes, strikeouts, checkboxes)
 * - All saved as JSON (not XFDF)
 * - Wires up to existing permission/save hooks
 */

import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { MutationFunctionOptions, useQuery, useMutation } from '@apollo/client';
import { useDocumentState } from '../../../contexts/DocumentContext';
import { permissionLevelToBooleans } from '../../../types/counterparty-permissions';
import { DocumentPermissions } from '../shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { ImportedDocumentSupportedContentTypes } from '../../../components/shared/modals/fileUploadModal/types';
import { DocumentStatusEnum } from '../../../shared/constants/document';
import { DocumentSourceEnum } from '../../../components/document-wizard/document-editor/constants/EditorConstats';
import { CounterpartyPermissionLevel, canFillForms } from '../../../types/counterparty-permissions';

// PDF.js components
import { PDFJSViewer } from '../../../components/pdf-viewer/PDFJSViewer';
import { useFieldPlacements, FieldPlacement } from '../../../components/pdf-viewer/hooks/useFieldPlacements';
// Removed: useFormFields - PDFJSViewer now manages fields internally
import { PDFPageInfo } from '../../../components/pdf-viewer/types';
import { getDocumentById } from '../../../api/queries_v2';
import { getCurrentUserBasic } from '../../../api/queries';
import { setCounterpartyPermissionLevel as setCounterpartyPermissionLevelMutation } from '../../../api/mutations';
import * as pdfjsLib from 'pdfjs-dist';

import styles from '../../signing/styles.module.scss';

export interface DocumentPagePdfJsProps {
  lastUpdated: string;
  documentTitle: string;
  isDocumentCreator: boolean;
  documentId: string;
  permissions: DocumentPermissions;
  handleClose: () => void;
  pdfUrl: string;
  updateDocumentPdfWithFormValues: (options: MutationFunctionOptions) => Promise<any>;
  isDocumentLocked: boolean;
  dealId: string;
  teamId: string;
  documentContentType: ImportedDocumentSupportedContentTypes;
  documentStatus: string;
  documentSource: DocumentSourceEnum;
  hasCurrentTeamPossession: boolean;
  canEdit: boolean;
  canComment: boolean;
  isDocumentEditable?: boolean;
  isDocumentInReview?: boolean;
}

const DocumentPagePdfJs = ({
  lastUpdated,
  documentTitle,
  documentId,
  permissions,
  handleClose,
  pdfUrl,
  isDocumentLocked,
  isDocumentCreator,
  dealId,
  teamId,
  documentStatus,
  documentSource,
  hasCurrentTeamPossession,
  canEdit,
  canComment,
  isDocumentEditable,
  isDocumentInReview,
}: DocumentPagePdfJsProps) => {
  const viewer = useRef<HTMLDivElement>(null);
  const [pagesInfo, setPagesInfo] = useState<PDFPageInfo[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [pdfDocument, setPdfDocument] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pages, setPages] = useState<Array<{ id: string; position: number }>>([]);
  const { updateDocumentState } = useDocumentState();

  const isSigningProcessStarted = [
    DocumentStatusEnum.PREPARING_TO_SIGN,
    DocumentStatusEnum.SIGNING,
    DocumentStatusEnum.EXECUTED,
  ].includes(documentStatus as DocumentStatusEnum);
  const isDocumentExecuted = documentStatus === DocumentStatusEnum.EXECUTED;

  // Determine if editing is allowed
  const isEditingAllowed = useMemo(() => {
    if (isDocumentLocked || !canEdit || isSigningProcessStarted || isDocumentExecuted) {
      return false;
    }
    if (isDocumentInReview && !isDocumentEditable) {
      return false;
    }
    return true;
  }, [isDocumentLocked, canEdit, isSigningProcessStarted, isDocumentExecuted, isDocumentInReview, isDocumentEditable]);

  // Get current user info
  const { data: userData } = useQuery(getCurrentUserBasic);
  const currentUser = userData?.currentUser;

  // Load existing field placements and annotations from GraphQL
  const { data: documentData, refetch: refetchDocument } = useQuery(getDocumentById, {
    variables: { documentId },
    skip: !documentId,
  });

  // Memoize initialFields to prevent unnecessary re-renders
  const initialFields: FieldPlacement[] = useMemo(() => {
    const fields = (documentData?.getDocumentById?.fieldPlacements || []) as FieldPlacement[];
    return fields;
  }, [documentData?.getDocumentById?.fieldPlacements]);

  // Get counterparty permission level from document data (what counterparties can do)
  // This is the database value and should NOT be changed based on who's viewing
  const counterpartyPermissionLevel = useMemo(() => {
    return (documentData?.getDocumentById?.counterpartyPermissionLevel as CounterpartyPermissionLevel) || CounterpartyPermissionLevel.CommentOnly;
  }, [documentData?.getDocumentById?.counterpartyPermissionLevel]);

  // Determine if current user is a creator team member (not counterparty/receiver)
  // CRITICAL: Only isDocumentCreator determines team membership, NOT hasCurrentTeamPossession
  // hasCurrentTeamPossession controls document locking, not permission levels
  const isCreatorTeamMember = useMemo(() => {
    return isDocumentCreator;
  }, [isDocumentCreator]);

  // Map team permissions to CounterpartyPermissionLevel for UI controls
  const effectivePermissionLevel = useMemo(() => {
    if (isCreatorTeamMember) {
      // Creator team: Use team-based permissions BUT respect approval request restrictions
      // When document is in internal review with permitEditingDuringApproval=false,
      // isDocumentEditable will be false, forcing CommentOnly mode
      if (permissions.hasEditPermission && isDocumentEditable) {
        return CounterpartyPermissionLevel.FullEditing;
      } else if (permissions.hasCommentPermission) {
        return CounterpartyPermissionLevel.CommentOnly;
      }
      return CounterpartyPermissionLevel.CommentOnly; // Fallback
    }

    // Counterparty/receiver: Use database counterpartyPermissionLevel
    return counterpartyPermissionLevel;
  }, [isCreatorTeamMember, permissions, counterpartyPermissionLevel, isDocumentEditable]);

  // Determine if user can access Annotate/Forms toggle
  // Available to: (1) creator team with edit permission, OR (2) counterparties with Full Editing permission
  const canAccessFormsToggle = useMemo(() => {
    if (isCreatorTeamMember) {
      return permissions.hasEditPermission;
    }
    // Counterparties can access toggle if they have Full Editing permission
    return effectivePermissionLevel === CounterpartyPermissionLevel.FullEditing;
  }, [isCreatorTeamMember, permissions, effectivePermissionLevel]);

  // Determine if user can fill form fields based on permission level
  // Form filling requires: (1) document not locked/executed, AND (2) appropriate permission level
  const canUserFillForms = useMemo(() => {
    // Basic checks: document must not be locked, executed, or in signing process
    if (isDocumentLocked || isSigningProcessStarted || isDocumentExecuted) {
      return false;
    }
    // If in review, check if editing is allowed
    if (isDocumentInReview && !isDocumentEditable) {
      return false;
    }
    // Check if user's permission level allows form filling
    return canFillForms(effectivePermissionLevel);
  }, [isDocumentLocked, isSigningProcessStarted, isDocumentExecuted, isDocumentInReview, isDocumentEditable, effectivePermissionLevel]);

  // Mutation for updating counterparty permissions
  const [setCounterpartyPermissionLevel] = useMutation(setCounterpartyPermissionLevelMutation);

  // Field placements management with auto-save (for custom annotations)
  const {
    fields,
    addField: addFieldPlacement,
    updateField: updateFieldPlacement,
    deleteField: deleteFieldPlacement,
    saving: savingFields,
  } = useFieldPlacements({
    documentId,
    initialFields,
    onSaveSuccess: () => {
      // No refetch needed - Apollo automatically updates cache when mutation returns id + fields
      // Timestamp update handled by main onPdfSaveSuccess callback
    },
    onSaveError: (error) => {
      console.error('Field placement save failed:', error);
    },
  });

  // Form fields management (for embedded PDF form fields)
  // Fields are loaded from savedPdfFormFields prop and managed in PDFJSViewer's allFields state

  const saving = savingFields;

  /**
   * Handle PDF.js viewer ready - capture pages info and PDF document for form field extraction
   */
  const handleViewerReady = useCallback((pages: PDFPageInfo[]) => {
    setPagesInfo(pages);
  }, []);

  /**
   * Handle PDF document loaded - store reference for form field extraction
   */
  const handlePdfLoaded = useCallback((doc: pdfjsLib.PDFDocumentProxy) => {
    setPdfDocument(doc);
  }, []);

  /**
   * Handle counterparty permissions change
   */
  const handleChangePermissions = useCallback(async (level: CounterpartyPermissionLevel) => {
    try {
      // Call GraphQL mutation
      await setCounterpartyPermissionLevel({
        variables: {
          documentId,
          permissionLevel: level,
        },
      });

      // Update document state (for slideout dropdown sync)
      updateDocumentState({ counterpartyPermissionLevel: level, ...permissionLevelToBooleans(level) });

      // Refetch to update UI
      await refetchDocument();

      // Dispatch custom event for cross-UI sync (matches Apryse pattern)
      window.dispatchEvent(new CustomEvent('propdocs:permissionChanged', {
        detail: { type: 'level', value: level },
      }));
    } catch (error) {
      console.error('Failed to update counterparty permissions:', error);
    }
  }, [documentId, setCounterpartyPermissionLevel, refetchDocument, updateDocumentState]);

  /**
   * Listen for permission changes from slideouts to keep toolbar in sync
   */
  useEffect(() => {
    const syncHandler = (event: Event) => {
      const { detail } = event as CustomEvent<{ type: string; value: string }>;
      if (detail?.type === 'level' && detail?.value) {
        // Refetch document to update counterpartyPermissionLevel prop
        refetchDocument();
      }
    };

    window.addEventListener('propdocs:permissionChanged', syncHandler);
    return () => window.removeEventListener('propdocs:permissionChanged', syncHandler);
  }, [refetchDocument]);

  const dragOver = (e: any) => {
    e.preventDefault();
    return false;
  };

  const drop = (e: any) => {
    e.preventDefault();
    return false;
  };

  return (
    <div
      className={styles.pdfViewerContainer}
      ref={viewer}
      onDragOver={dragOver}
      onDrop={drop}
    >
      <PDFJSViewer
        pdfUrl={pdfUrl}
        mode={isEditingAllowed ? 'edit' : 'read-only'}
        showControls={!isDocumentExecuted}
        showThumbnails={!isDocumentExecuted}
        onPagesReady={handleViewerReady}
        onDocumentLoad={handlePdfLoaded}
        onPagesLoad={setPages}
        documentId={documentId}
        teamId={teamId}
        documentTitle={documentTitle}
        lastSaved={lastUpdated}
        counterpartyPermissionLevel={counterpartyPermissionLevel}
        effectivePermissionLevel={effectivePermissionLevel}
        canAccessFormsToggle={canAccessFormsToggle}
        onChangePermissions={handleChangePermissions}
        canEditPermissions={isDocumentCreator && isEditingAllowed}
        currentUserId={currentUser?.id}
        currentUserName={currentUser?.fullName || 'Current User'}
        fieldOverlayProps={
          documentStatus === DocumentStatusEnum.PREPARING_TO_SIGN && !isDocumentExecuted
            ? {
                fields,
                selectedFieldId,
                onSelectField: setSelectedFieldId,
                onUpdateField: updateFieldPlacement,
                onDeleteField: deleteFieldPlacement,
              }
            : undefined
        }
        hideComments={isSigningProcessStarted}
        hideFormFieldsAndAnnotations={isSigningProcessStarted}
        savedPdfFormFields={documentData?.getDocumentById?.pdfFormFields || []}
        savedPdfAnnotations={documentData?.getDocumentById?.pdfAnnotations || []}
        onPdfSaveSuccess={(timestamp) => {
          // No refetch needed - use timestamp from mutation response (Apollo auto-updates cache)
          // Update document state with timestamp for "Last saved" indicator in header
          if (timestamp) {
            updateDocumentState({ lastUpdated: timestamp });
          }
        }}
      />
    </div>
  );
};

export default DocumentPagePdfJs;
